import { useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  CloudUpload,
  Download,
  X,
} from '@/shared/ui/icons';
import { useUploadContext } from '../../contexts/UploadContext';
import { summariseTransfers, transferHeaderLabel } from './transferSummary';
import { TransferRow } from './TransferRow';
import { cn } from '../../shared/ui/utils';
import { Progress } from '../../shared/ui/progress';

export function UploadProgressPanel() {
  const { jobs, isExpanded, setExpanded, dismissCompleted } = useUploadContext();
  const [hidden, setHidden] = useState(false);

  const summary = useMemo(() => summariseTransfers(jobs), [jobs]);

  // Auto-dismiss once every upload finishes successfully. Keep the panel up
  // when something failed so the user can read the error before it clears.
  useEffect(() => {
    if (!summary.allDone || summary.failed > 0) return;
    const timer = setTimeout(() => dismissCompleted(), 2500);
    return () => clearTimeout(timer);
  }, [summary.allDone, summary.failed, dismissCompleted]);

  if (jobs.length === 0 || hidden) return null;

  const headerLabel = transferHeaderLabel(summary);

  return (
    <div
      className={cn(
        // Fixed bottom-right on desktop, full-width bottom on phone.
        'fixed z-[60] bg-popover/95 backdrop-blur-xl border border-border/60 shadow-2xl',
        'rounded-2xl overflow-hidden font-apple',
        'bottom-4 right-4 left-4 sm:left-auto sm:w-[360px]',
      )}
    >
      {/* A row, not a button wrapping a button: the dismiss control has to be
          its own click target, and nesting it inside the expand button made it
          invalid markup that swallowed its own clicks in some browsers. */}
      <div className="flex items-center gap-2 pr-2">
        <button
          type="button"
          onClick={() => setExpanded(!isExpanded)}
          className="flex-1 min-w-0 flex items-center gap-2 px-4 py-3 hover:bg-muted/40 transition-colors text-left"
          aria-expanded={isExpanded}
        >
          {summary.allDownloads ? (
            <Download className="w-4 h-4 shrink-0 text-brand" strokeWidth={2} />
          ) : (
            <CloudUpload className="w-4 h-4 shrink-0 text-brand" strokeWidth={2} />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-foreground truncate">
              {headerLabel}
            </div>
            {!summary.allDone && summary.percent !== null && (
              <Progress
                value={summary.percent}
                aria-label="Overall transfer progress"
                className="mt-1 h-[3px] bg-muted/50"
                indicatorClassName="bg-brand"
              />
            )}
          </div>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" strokeWidth={2} />
          ) : (
            <ChevronUp className="w-4 h-4 shrink-0 text-muted-foreground" strokeWidth={2} />
          )}
        </button>
        {summary.allDone && (
          <button
            type="button"
            onClick={() => {
              dismissCompleted();
              setHidden(false);
            }}
            className="shrink-0 p-1 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        )}
      </div>

      {isExpanded && (
        <div className="max-h-[40vh] overflow-y-auto border-t border-border/40">
          {jobs.map((job) => (
            <TransferRow key={job.id} job={job} />
          ))}
        </div>
      )}
    </div>
  );
}
